import { useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// Permanent challenge definitions - these don't reset daily
// Rewards are balanced to avoid excessive XP
const PERMANENT_CHALLENGES = [
  // Easy challenges (low XP rewards)
  { id: 'win_3_levels', title: 'Victory March', description: 'Win 3 levels', target: 3, type: 'levels_completed', reward: 150, icon: '🏆' },
  { id: 'collect_100_fruits', title: 'Fruit Frenzy', description: 'Collect 100 fruits', target: 100, type: 'total_fruits', reward: 200, icon: '🍎' },
  { id: 'score_5000', title: 'Score Hunter', description: 'Score 5,000 XP total', target: 5000, type: 'total_xp', reward: 250, icon: '⭐' },
  { id: 'create_3_striped', title: 'Stripe Maker', description: 'Create 3 striped fruits', target: 3, type: 'striped_fruits', reward: 100, icon: '⚡' },
  { id: 'create_2_wrapped', title: 'Wrapper', description: 'Create 2 wrapped fruits', target: 2, type: 'wrapped_fruits', reward: 100, icon: '🎁' },
  
  // Medium challenges
  { id: 'use_sword', title: 'Sword Master', description: 'Use the sword ability 5 times', target: 5, type: 'sword_used', reward: 200, icon: '⚔️' },
  { id: 'win_no_items', title: 'Purist', description: 'Win 3 levels without using items', target: 3, type: 'perfect_wins', reward: 300, icon: '🎯' },
  { id: 'combo_5', title: 'Combo King', description: 'Get 10 special combos', target: 10, type: 'special_combos', reward: 250, icon: '🔥' },
  { id: 'collect_50_bananas', title: 'Banana Bonanza', description: 'Collect 50 bananas', target: 50, type: 'banana', reward: 150, icon: '🍌' },
  { id: 'play_5_games', title: 'Dedicated Player', description: 'Complete 5 levels', target: 5, type: 'levels_completed', reward: 200, icon: '🎮' },
  
  // Hard challenges
  { id: 'disco_combo', title: 'Disco Fever', description: 'Create 5 disco combos', target: 5, type: 'disco_combos', reward: 400, icon: '🪩' },
  { id: 'perfect_level', title: 'Perfect Play', description: 'Win 5 levels perfectly', target: 5, type: 'perfect_wins', reward: 500, icon: '✨' },
  { id: 'high_score', title: 'High Roller', description: 'Earn 25,000 XP total', target: 25000, type: 'total_xp', reward: 500, icon: '💰' },
  { id: 'special_combo', title: 'Special Agent', description: 'Trigger 20 special combos', target: 20, type: 'special_combos', reward: 400, icon: '💥' },
  
  // Expert challenges
  { id: 'fruit_master', title: 'Fruit Master', description: 'Collect 500 fruits', target: 500, type: 'total_fruits', reward: 600, icon: '🍇' },
  { id: 'level_champion', title: 'Level Champion', description: 'Complete 15 levels', target: 15, type: 'levels_completed', reward: 500, icon: '🏅' },
  { id: 'disco_master', title: 'Disco Master', description: 'Create 15 disco combos', target: 15, type: 'disco_combos', reward: 600, icon: '🕺' },
  { id: 'combo_expert', title: 'Combo Expert', description: 'Trigger 50 special combos', target: 50, type: 'special_combos', reward: 700, icon: '⚡' },
  
  // Master challenges
  { id: 'fruit_legend', title: 'Fruit Legend', description: 'Collect 1,000 fruits', target: 1000, type: 'total_fruits', reward: 1000, icon: '👑' },
  { id: 'score_legend', title: 'Score Legend', description: 'Earn 100,000 XP total', target: 100000, type: 'total_xp', reward: 1500, icon: '🌟' },
];

// Get challenges (all permanent, not rotating)
export const getTodaysChallenges = () => {
  return PERMANENT_CHALLENGES;
};

// No longer needed but kept for backwards compatibility
export const getTimeUntilReset = () => {
  return { hours: 0, minutes: 0, totalMs: 0 };
};

export const formatTimeUntilReset = () => {
  return "Permanent";
};

export function useDailyChallenges() {
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [challenges] = useState(PERMANENT_CHALLENGES);
  
  // Get stats from localStorage (same source as useChallenges)
  const getStats = useCallback(() => {
    const saved = localStorage.getItem('fruitCrush_stats');
    return saved ? JSON.parse(saved) : {};
  }, []);
  
  // Load completed challenges status from backend
  const loadChallenges = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      // Load from localStorage if no token
      const saved = localStorage.getItem('fruitCrush_permanentChallenges');
      if (saved) {
        setCompleted(JSON.parse(saved));
      }
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/daily-challenges`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompleted(data.completed || []);
        // Also save to localStorage
        localStorage.setItem('fruitCrush_permanentChallenges', JSON.stringify(data.completed || []));
      }
    } catch (error) {
      console.error('Failed to load challenges:', error);
      // Fallback to localStorage
      const saved = localStorage.getItem('fruitCrush_permanentChallenges');
      if (saved) {
        setCompleted(JSON.parse(saved));
      }
    }
    
    setLoading(false);
  }, []);
  
  useEffect(() => {
    loadChallenges();
  }, [loadChallenges]);
  
  // Complete a challenge
  const completeChallenge = useCallback(async (challengeId) => {
    if (completed.includes(challengeId)) return { success: false };
    
    const token = localStorage.getItem(TOKEN_KEY);
    
    // Find the challenge to get reward
    const challenge = PERMANENT_CHALLENGES.find(c => c.id === challengeId);
    if (!challenge) return { success: false };
    
    if (token) {
      try {
        const response = await fetch(`${API_URL}/api/daily-challenges/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ challenge_id: challengeId })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const newCompleted = [...completed, challengeId];
            setCompleted(newCompleted);
            localStorage.setItem('fruitCrush_permanentChallenges', JSON.stringify(newCompleted));
            return { success: true, stars: data.stars, xp: challenge.reward };
          }
        }
      } catch (error) {
        console.error('Failed to complete challenge:', error);
      }
    }
    
    // Fallback to local storage
    const newCompleted = [...completed, challengeId];
    setCompleted(newCompleted);
    localStorage.setItem('fruitCrush_permanentChallenges', JSON.stringify(newCompleted));
    return { success: true, xp: challenge.reward };
  }, [completed]);
  
  // Check if a challenge is complete based on stats
  const checkChallengeCompletion = useCallback((challenge) => {
    const stats = getStats();
    const progress = stats[challenge.type] || 0;
    return progress >= challenge.target;
  }, [getStats]);
  
  // Update progress for a challenge type (called from game)
  // This updates the permanent stats stored in localStorage
  const updateProgress = useCallback((type, value) => {
    const saved = localStorage.getItem('fruitCrush_stats');
    const current = saved ? JSON.parse(saved) : {};
    const newStats = {
      ...current,
      [type]: (current[type] || 0) + value
    };
    localStorage.setItem('fruitCrush_stats', JSON.stringify(newStats));
  }, []);
  
  // Get challenges with status
  const getChallengesWithStatus = useCallback(() => {
    const stats = getStats();
    return challenges.map(challenge => ({
      ...challenge,
      isCompleted: completed.includes(challenge.id),
      canClaim: !completed.includes(challenge.id) && checkChallengeCompletion(challenge),
      progress: stats[challenge.type] || 0
    }));
  }, [challenges, completed, getStats, checkChallengeCompletion]);
  
  return {
    loading,
    completed,
    todaysChallenges: getChallengesWithStatus(),
    starsEarned: completed.length,
    totalChallenges: challenges.length,
    completeChallenge,
    updateProgress,
    timeUntilReset: formatTimeUntilReset,
    reload: loadChallenges
  };
}
